import { memo, useCallback, useState } from "react";
import { Plus } from "lucide-react";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";
import { useStockAvailability } from "../../hooks/useStockAvailability";
import { isFullyAvailable } from "../../utils/stock-availability.utils";
import { StockAvailabilityAlert } from "./StockAvailabilityAlert";

interface StockBarcodeAdderProps {
  expectedOutAt: string;
  expectedInAt: string;
  existingStockIds: number[];
  onAdd: (item: StockAvailabilityResponseDTO) => void;
}

/**
 * Input de barcode com validação de disponibilidade na data do aluguel.
 *
 * Ao confirmar (Enter ou clicar adicionar), chama
 * `useStockAvailability` no intervalo `expectedOutAt..expectedInAt`.
 * Se a peça estiver totalmente disponível, adiciona direto. Se houver
 * dias indisponíveis, abre um Dialog com `StockAvailabilityAlert`
 * e exige confirmação explícita — alinhado ao requisito de não bloquear
 * mas alertar de forma chamativa.
 */
export const StockBarcodeAdder = memo(function StockBarcodeAdder({
  expectedOutAt,
  expectedInAt,
  existingStockIds,
  onAdd,
}: StockBarcodeAdderProps) {
  const [barcode, setBarcode] = useState("");
  const [pendingItem, setPendingItem] =
    useState<StockAvailabilityResponseDTO | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { check, checking } = useStockAvailability({
    startDate: expectedOutAt,
    endDate: expectedInAt,
  });

  const reset = useCallback(() => {
    setBarcode("");
    setPendingItem(null);
    setFeedback(null);
  }, []);

  const handleAdd = useCallback(async () => {
    const trimmed = barcode.trim();
    if (!trimmed) return;
    if (!expectedOutAt || !expectedInAt) {
      setFeedback("Defina as datas de retirada e devolução antes");
      return;
    }
    setFeedback(null);

    const result = await check(trimmed);
    if (!result.data) {
      setFeedback(result.error ?? "Erro ao consultar disponibilidade");
      return;
    }
    if (existingStockIds.includes(result.data.stockId)) {
      setFeedback("Esta peça já foi adicionada");
      return;
    }

    if (isFullyAvailable(result.data.statusByDate)) {
      onAdd(result.data);
      reset();
      return;
    }

    setPendingItem(result.data);
  }, [
    barcode,
    expectedOutAt,
    expectedInAt,
    existingStockIds,
    check,
    onAdd,
    reset,
  ]);

  const confirmAdd = useCallback(() => {
    if (!pendingItem) return;
    onAdd(pendingItem);
    reset();
  }, [pendingItem, onAdd, reset]);

  const cancelAdd = useCallback(() => {
    setPendingItem(null);
  }, []);

  return (
    <div className="space-y-2">
      <Label htmlFor="stock-barcode">Adicionar peça por código de barras</Label>
      <div className="flex gap-2">
        <Input
          id="stock-barcode"
          placeholder="Escaneie ou digite o código"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleAdd();
            }
          }}
        />
        <Button
          type="button"
          onClick={handleAdd}
          disabled={checking || !barcode.trim()}
          className="gap-2"
        >
          {checking ? (
            <Spinner className="w-4 h-4" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          Adicionar
        </Button>
      </div>
      {feedback ? <p className="text-xs text-destructive">{feedback}</p> : null}

      <Dialog
        open={pendingItem !== null}
        onOpenChange={(value) => !value && cancelAdd()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Peça com restrição de disponibilidade</DialogTitle>
            <DialogDescription>
              A peça {pendingItem?.barcode} não está totalmente disponível no
              período selecionado. Você pode incluí-la mesmo assim — o alerta
              ficará visível na lista até a finalização do aluguel.
            </DialogDescription>
          </DialogHeader>
          {pendingItem ? (
            <StockAvailabilityAlert statusByDate={pendingItem.statusByDate} />
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={cancelAdd}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmAdd}>
              Adicionar mesmo assim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
